import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import setActiveTab from "@tecumseh/Handlers/SearchResultsTabsHandler";
import CustomApplicationState from "@tecumseh/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState & CustomApplicationState) => {
    const { productFilters, isSearchPage } = state.pages.productList;
    const { searchResultsTab } = state.TECData;
    return {
        view: state.pages.productList.view,
        location: getLocation(state),
        query: productFilters.query,
        isSearchPage,
        activeTab: searchResultsTab.activeTab,
    };
};

const mapDispatchToProps = {
    setActiveTab,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface ProductListColumnsStyles {
    containerWrapper?: InjectableCss;
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnTableViewGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnTableViewGridItem?: GridItemProps;
}

export const columnsStyles: ProductListColumnsStyles = {
    containerWrapper:{
        css: css`
            min-height: 100vh;
        `,
    },
    container: {
        gap: 20,
    },
    leftColumnGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    leftColumnTableViewGridItem: {
        width: [12, 12, 0, 0, 0],
    },
    rightColumnGridItem: {
        width: [12, 12, 9, 9, 9],
    },
    rightColumnTableViewGridItem: {
        width: 12,
    },
};

const styles = columnsStyles;

const ProductListColumns: FC<Props> = ({
    id,
    view,
    location,
    query,
    isSearchPage,
    history,
    setActiveTab,
    activeTab,
}) => {
    const leftColumnGridItemStyle = styles.leftColumnGridItem;
    const rightColumnGridItemStyle = styles.rightColumnGridItem;

    useEffect(() => {
        const queryParams = parseQueryString<{ activeTab?: string }>(location.search);
        if (isSearchPage) {
            setActiveTab({
                activeTab: queryParams.activeTab || "products",
            });
        }
    }, [location.search, isSearchPage]);

    if (isSearchPage) {
        if (activeTab === "documents" || activeTab === "content") {
            return null;
        }

        return (
            <GridContainer {...styles.container}>
                <GridItem {...leftColumnGridItemStyle}>
                    <Zone contentId={id} zoneName="Content00" />
                </GridItem>
                <GridItem {...rightColumnGridItemStyle}>
                    <Zone contentId={id} zoneName="Content01" />
                </GridItem>
            </GridContainer>
        );
    }

    return (<StyledWrapper {...styles.containerWrapper}>
            <GridContainer {...styles.container}>
                <GridItem {...leftColumnGridItemStyle}>
                    <Zone contentId={id} zoneName="Content00" />
                </GridItem>
                <GridItem {...rightColumnGridItemStyle}>
                    <Zone contentId={id} zoneName="Content01" />
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(ProductListColumns)),
    definition: {
        group: "Product List",
        displayName: "Columns",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
